"use client";

import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";

// Social links skeleton
export function SocialLinksSkeleton() {
  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        placeContent: { xs: "center", md: "center" },
        textAlign: { xs: "center", md: "left" },
        pt: 1.75,
        pb: { xs: 2, md: 1.75 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          p: 0,
          justifyContent: { xs: "center", md: "center" },
          alignItems: { xs: "center", md: "center" },
        }}
      >
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width={90}
            height={32}
            animation="wave"
          />
        ))}
      </Box>
    </Box>
  );
}

// Exact Gallery skeleton that matches the structure of the actual gallery component
export function GallerySkeletonExact() {
  // Create an array of items with varying heights to mimic a masonry layout
  const items = Array.from({ length: 9 }).map((_, index) => ({
    height: 900 + (index % 3) * 50,
  }));

  return (
    <Box
      maxWidth="xl"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column", // Stack heading and gallery vertically
        alignItems: "center",
        justifyContent: "center", // Center content vertically if needed
        mx: "auto", // Center the box horizontally
      }}
    >
      <ImageList
        variant="masonry"
        sx={{
          columnCount: {
            xs: "1 !important",
            sm: "1 !important",
            md: "2 !important",
            lg: "3 !important",
            xl: "3 !important",
          },
          width: "100%",
          // Ensure consistent spacing
          m: 0,
          p: 0,
        }}
        gap={8}
      >
        {items.map((item, index) => (
          <ImageListItem key={index} sx={{ mb: 2, overflow: "hidden" }}>
            <Skeleton
              variant="rounded"
              width="100%"
              height={item.height}
              animation="wave"
              sx={{
                borderRadius: 1,
                transform: "scale(1)", // Prevent any transform issues
              }}
            />
          </ImageListItem>
        ))}
      </ImageList>
    </Box>
  );
}

// Characteristics skeleton
export function CharacteristicsSkeleton() {
  return (
    <Container maxWidth="xl" sx={{ p: 0.5, mt: 3 }}>
      <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width={100}
            height={32}
            animation="wave"
          />
        ))}
      </Box>
    </Container>
  );
}

// Bio section skeleton
export function BioSectionSkeleton() {
  return (
    <Container maxWidth="xl" sx={{ p: 0.5, mt: 3 }}>
      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <Skeleton variant="text" width="20%" height={30} sx={{ mb: 1 }} />
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: 330,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Stack spacing={1}>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="95%" height={20} />
              <Skeleton variant="text" width="85%" height={20} />
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="80%" height={20} />
            </Stack>
          </Paper>
        </Grid>

        <Grid xs={12} md={6}>
          <Skeleton variant="text" width="40%" height={30} sx={{ mb: 1 }} />
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: 140,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Stack spacing={1}>
              <Skeleton variant="text" width="100%" height={20} />
              <Skeleton variant="text" width="90%" height={20} />
              <Skeleton variant="text" width="95%" height={20} />
            </Stack>
          </Paper>

          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width="30%" height={30} sx={{ mb: 1 }} />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Skeleton variant="rounded" width={80} height={32} />
              <Skeleton variant="rounded" width={80} height={32} />
              <Skeleton variant="rounded" width={80} height={32} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

// Services skeleton
export function ServicesSkeleton() {
  return (
    <Container maxWidth="xl" sx={{ p: 0.5, mt: 3 }}>
      <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />

      {/* Included services */}
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              width={120}
              height={32}
              animation="wave"
            />
          ))}
        </Box>
      </Box>

      {/* Additional services */}
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width="35%" height={24} sx={{ mb: 1 }} />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              width={130}
              height={32}
              animation="wave"
            />
          ))}
        </Box>
      </Box>

      {/* Equipment section */}
      <Box>
        <Skeleton variant="text" width="45%" height={24} sx={{ mb: 1 }} />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rounded"
              width={140}
              height={32}
              animation="wave"
            />
          ))}
        </Box>
      </Box>
    </Container>
  );
}
